/**
 * Express Processing Component
 *
 * Automatically suggests adding Express Processing to cart based on regular product count.
 * Handles adding, updating, and removing Express Processing variants intelligently.
 *
 * Product ID: 10301728325898
 * Variant Mapping:
 * - 1-3 products: 52083101925642 (5€)
 * - 4-6 products: 52083101958410 (8€)
 * - 7-10 products: 52083101991178 (12€)
 * - 11+ products: 52083102023946 (15€)
 */

// Log variant mapping for debugging
console.log('🚀 [Express Processing] Variant Mapping:');
console.table([
  { 'Product Range': '1-3 products', 'Variant ID': 52083101925642, 'Price': '5.00€' },
  { 'Product Range': '4-6 products', 'Variant ID': 52083101958410, 'Price': '8.00€' },
  { 'Product Range': '7-10 products', 'Variant ID': 52083101991178, 'Price': '12.00€' },
  { 'Product Range': '11+ products', 'Variant ID': 52083102023946, 'Price': '15.00€' }
]);

class ExpressProcessingPrompt extends HTMLElement {
  constructor() {
    super();

    this.EXPRESS_PRODUCT_ID = 10301728325898;
    this.VARIANT_MAP = [
      { min: 1, max: 3, id: 52083101925642, price: 500 },
      { min: 4, max: 6, id: 52083101958410, price: 800 },
      { min: 7, max: 10, id: 52083101991178, price: 1200 },
      { min: 11, max: Infinity, id: 52083102023946, price: 1500 }
    ];

    this.isProcessing = false;
    this.retryCount = 0;
    this.maxRetries = 1;

    // Debounced cart update handler (300ms like cart.js)
    this.debouncedCartUpdate = this.debounce(this.handleCartUpdate.bind(this), 300);
  }

  connectedCallback() {
    // Called when element is inserted into DOM
    console.log('🔌 [Express Processing] ============================================');
    console.log('🔌 [Express Processing] Element connected to DOM at:', new Date().toISOString());
    console.log('🔌 [Express Processing] Parent element:', this.parentElement?.className);
    console.log('🔌 [Express Processing] ============================================');

    // Use setTimeout to ensure DOM is fully ready (especially after AJAX re-render)
    setTimeout(() => {
      this.setupElement();
    }, 0);
  }

  setupElement() {
    console.log('⚙️  [Express Processing] Setting up element...');

    // Query elements after the element is in the DOM
    this.button = this.querySelector('.express-processing__button');
    this.errorElement = this.querySelector('.express-processing__error');

    console.log('🔍 [Express Processing] Button found:', !!this.button);
    console.log('🔍 [Express Processing] Button element:', this.button);

    if (!this.button) {
      console.error('❌ [Express Processing] Button not found! Looking for .express-processing__button');
      console.log('🔍 [Express Processing] Element HTML:', this.innerHTML);
      console.log('🔍 [Express Processing] Element children:', this.children);
      return;
    }

    this.init();
  }

  init() {
    // Prevent multiple initializations
    if (this.initialized) {
      console.log('⚠️  [Express Processing] Already initialized, skipping...');
      return;
    }

    console.log('🚀 [Express Processing] Component initialized');
    console.log('🚀 [Express Processing] Product ID:', this.EXPRESS_PRODUCT_ID);
    console.log('🚀 [Express Processing] Initial variant:', this.dataset.variantId);
    console.log('🚀 [Express Processing] Initial count:', this.dataset.regularCount);

    // Add click handler for button
    if (this.button) {
      console.log('✅ [Express Processing] Button exists, attaching click listener...');
      this.button.addEventListener('click', this.handleAddClick.bind(this));
      console.log('✅ [Express Processing] Button event listener attached successfully');

      // Test if button is clickable
      console.log('🔍 [Express Processing] Button disabled:', this.button.disabled);
      console.log('🔍 [Express Processing] Button type:', this.button.type);
    } else {
      console.error('❌ [Express Processing] Button is NULL - cannot attach event listener!');
    }

    // Listen for cart updates (only once)
    document.addEventListener('cart:updated', this.debouncedCartUpdate);
    console.log('🚀 [Express Processing] Listening for cart:updated events');

    // Listen for PubSub cart updates (if available)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.debouncedCartUpdate);
      console.log('🚀 [Express Processing] Subscribed to PUB_SUB_EVENTS.cartUpdate');
    }

    this.initialized = true;
  }

  /**
   * Debounce utility to prevent excessive API calls
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Handle cart update events
   */
  async handleCartUpdate(event) {
    console.log('🔄 [Express Processing] Cart update event triggered');

    if (this.isProcessing) {
      console.log('⏸️  [Express Processing] Skipping - already processing');
      return;
    }

    try {
      // Fetch fresh cart data
      console.log('📡 [Express Processing] Fetching cart data from /cart.js');
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();
      console.log('📦 [Express Processing] Cart data received:', cart);
      console.log('📦 [Express Processing] Total items:', cart.item_count);
      console.log('📦 [Express Processing] Cart items:', cart.items);

      this.checkCartState(cart);
    } catch (error) {
      console.error('❌ [Express Processing] Error checking cart state:', error);
    }
  }

  /**
   * Check cart state and determine if Express Processing needs updating
   */
  checkCartState(cart) {
    console.log('🔍 [Express Processing] Checking cart state...');
    let regularProductCount = 0;
    let expressItem = null;

    // Count regular products and find Express Processing if present
    cart.items.forEach(item => {
      if (item.product_id === this.EXPRESS_PRODUCT_ID) {
        expressItem = item;
        console.log('✅ [Express Processing] Found Express item in cart:', {
          variant_id: item.variant_id,
          quantity: item.quantity,
          title: item.title
        });
      } else {
        regularProductCount += item.quantity;
        console.log('📝 [Express Processing] Regular product:', {
          title: item.title,
          quantity: item.quantity,
          product_id: item.product_id
        });
      }
    });

    console.log('🔢 [Express Processing] Regular product count:', regularProductCount);

    // If no regular products, hide prompt AND remove Express if it's in cart
    if (regularProductCount === 0) {
      console.log('🚫 [Express Processing] No regular products - hiding prompt');
      this.hidePrompt();

      // Remove Express product if it exists in cart
      if (expressItem) {
        console.log('🗑️  [Express Processing] Removing Express product from empty cart');
        this.removeExpressProcessing(expressItem);
      }

      return;
    }

    // If Express is in cart, hide prompt
    if (expressItem) {
      console.log('👀 [Express Processing] Express already in cart - hiding prompt');
      this.hidePrompt();

      // Check if Express variant is correct for current count
      const correctVariantId = this.getCorrectVariantId(regularProductCount);
      console.log('🎯 [Express Processing] Correct variant for', regularProductCount, 'products:', correctVariantId);
      console.log('🎯 [Express Processing] Current variant in cart:', expressItem.variant_id);

      if (expressItem.variant_id !== correctVariantId) {
        console.log('🔄 [Express Processing] Variant mismatch - auto-updating...');
        // Auto-update to correct variant
        this.updateExpressVariant(expressItem, correctVariantId);
      } else {
        console.log('✅ [Express Processing] Variant is correct - no update needed');
      }
      return;
    }

    // Show prompt with correct variant
    console.log('💡 [Express Processing] Showing prompt for', regularProductCount, 'products');
    this.showPrompt(regularProductCount);
  }

  /**
   * Get correct variant ID based on regular product count
   */
  getCorrectVariantId(count) {
    const variant = this.VARIANT_MAP.find(v => count >= v.min && count <= v.max);
    return variant ? variant.id : null;
  }

  /**
   * Get variant price
   */
  getVariantPrice(variantId) {
    const variant = this.VARIANT_MAP.find(v => v.id === variantId);
    return variant ? variant.price : 0;
  }

  /**
   * Show prompt with correct variant
   */
  showPrompt(regularProductCount) {
    console.log('👁️  [Express Processing] showPrompt() called with count:', regularProductCount);
    const variantId = this.getCorrectVariantId(regularProductCount);
    console.log('🎯 [Express Processing] Calculated variant ID:', variantId);

    if (!variantId) {
      console.warn('⚠️  [Express Processing] No variant ID found for count:', regularProductCount);
      return;
    }

    const price = this.getVariantPrice(variantId);
    console.log('💰 [Express Processing] Price for variant:', price);

    // Update button data and text
    this.button.dataset.variantId = variantId;
    this.dataset.variantId = variantId;
    this.dataset.regularCount = regularProductCount;

    // Update price in button text
    const priceElement = this.querySelector('.express-processing__price');
    if (priceElement && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      priceElement.textContent = Shopify.formatMoney(price, theme.moneyFormat || '{{amount_with_comma_separator}} €');
    } else {
      priceElement.textContent = (price / 100).toFixed(2) + '€';
    }

    this.removeAttribute('hidden');
    console.log('✅ [Express Processing] Prompt is now visible');
  }

  /**
   * Hide prompt
   */
  hidePrompt() {
    console.log('🙈 [Express Processing] Hiding prompt');
    this.setAttribute('hidden', '');
  }

  /**
   * Handle add button click
   */
  async handleAddClick(event) {
    event.preventDefault();
    console.log('🖱️  [Express Processing] Button clicked!');

    if (this.isProcessing) {
      console.log('⏸️  [Express Processing] Already processing - ignoring click');
      return;
    }

    const variantId = this.button.dataset.variantId;
    console.log('🎯 [Express Processing] Variant ID from button:', variantId);

    if (!variantId) {
      console.error('❌ [Express Processing] No variant ID on button!');
      return;
    }

    await this.addExpressProcessing(variantId);
  }

  /**
   * Add Express Processing to cart
   */
  async addExpressProcessing(variantId) {
    console.log('➕ [Express Processing] Adding to cart - Variant:', variantId);
    this.isProcessing = true;
    this.setLoadingState(true);
    this.hideError();

    try {
      const body = JSON.stringify({
        id: variantId,
        quantity: 1
      });

      console.log('📡 [Express Processing] Sending POST to:', theme.routes.cart_add_url + '.js');
      console.log('📡 [Express Processing] Request body:', body);

      const response = await fetch(theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });

      console.log('📨 [Express Processing] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [Express Processing] API error:', errorData);
        throw new Error(errorData.description || 'Failed to add Express Processing');
      }

      const result = await response.json();
      console.log('✅ [Express Processing] Successfully added to cart:', result);

      // Refresh cart sections
      console.log('🔄 [Express Processing] Refreshing cart sections...');
      await this.refreshCartSections();

      // Dispatch cart updated event
      console.log('📢 [Express Processing] Dispatching cart:updated event');
      document.dispatchEvent(new CustomEvent('cart:updated'));

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        console.log('📢 [Express Processing] Publishing PUB_SUB_EVENTS.cartUpdate');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-processing' });
      }

      this.retryCount = 0;
      this.hidePrompt();
      console.log('🎉 [Express Processing] Add complete!');
    } catch (error) {
      console.error('❌ [Express Processing] Add failed:', error);

      // Retry once if first attempt fails
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`🔄 [Express Processing] Retrying... (Attempt ${this.retryCount}/${this.maxRetries})`);
        setTimeout(() => this.addExpressProcessing(variantId), 1000);
      } else {
        console.error('❌ [Express Processing] Max retries reached');
        this.showError('Express-Bearbeitung konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.');
        this.retryCount = 0;
      }
    } finally {
      this.isProcessing = false;
      this.setLoadingState(false);
    }
  }

  /**
   * Update Express Processing variant in cart
   */
  async updateExpressVariant(expressItem, newVariantId) {
    if (this.isProcessing) {
      console.log('⏸️  [Express Processing] Already processing - skipping auto-update');
      return;
    }

    console.log('🔄 [Express Processing] Auto-updating variant...');
    console.log('🔄 [Express Processing] Old variant:', expressItem.variant_id);
    console.log('🔄 [Express Processing] New variant:', newVariantId);

    this.isProcessing = true;

    try {
      // Remove old variant
      const removeBody = JSON.stringify({
        id: expressItem.key,
        quantity: 0
      });

      console.log('🗑️  [Express Processing] Removing old variant:', expressItem.key);
      const removeResponse = await fetch(theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: removeBody
      });
      console.log('✅ [Express Processing] Old variant removed, status:', removeResponse.status);

      // Add new variant
      const addBody = JSON.stringify({
        id: newVariantId,
        quantity: 1
      });

      console.log('➕ [Express Processing] Adding new variant:', newVariantId);
      const addResponse = await fetch(theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: addBody
      });
      console.log('✅ [Express Processing] New variant added, status:', addResponse.status);

      // Refresh cart sections
      console.log('🔄 [Express Processing] Refreshing cart sections after update...');
      await this.refreshCartSections();

      // Dispatch cart updated event
      console.log('📢 [Express Processing] Dispatching cart:updated after auto-update');
      document.dispatchEvent(new CustomEvent('cart:updated'));

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-processing' });
      }

      console.log('🎉 [Express Processing] Auto-update complete!');
    } catch (error) {
      console.error('❌ [Express Processing] Update variant failed:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove Express Processing from cart when no regular products remain
   */
  async removeExpressProcessing(expressItem) {
    if (this.isProcessing) {
      console.log('⏸️  [Express Processing] Already processing - skipping auto-remove');
      return;
    }

    console.log('🗑️  [Express Processing] Auto-removing Express product...');
    console.log('🗑️  [Express Processing] Express item key:', expressItem.key);

    this.isProcessing = true;

    try {
      // Remove Express variant
      const removeBody = JSON.stringify({
        id: expressItem.key,
        quantity: 0
      });

      console.log('🗑️  [Express Processing] Removing Express product:', expressItem.key);
      const removeResponse = await fetch(theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: removeBody
      });
      console.log('✅ [Express Processing] Express product removed, status:', removeResponse.status);

      // Refresh cart sections
      console.log('🔄 [Express Processing] Refreshing cart sections after removal...');
      await this.refreshCartSections();

      // Dispatch cart updated event
      console.log('📢 [Express Processing] Dispatching cart:updated after auto-removal');
      document.dispatchEvent(new CustomEvent('cart:updated'));

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-processing' });
      }

      console.log('🎉 [Express Processing] Auto-removal complete!');
    } catch (error) {
      console.error('❌ [Express Processing] Remove Express failed:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Refresh cart sections using Shopify Section Rendering API
   */
  async refreshCartSections() {
    const sections = this.getSectionsToRender();
    const sectionIds = sections.map(section => section.section).filter(Boolean);

    if (sectionIds.length === 0) return;

    try {
      const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`);
      const sectionsHtml = await response.json();

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element && sectionsHtml[section.section]) {
          const elementToReplace = element.querySelector(section.selector) || element;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionsHtml[section.section], section.selector);
          }
        }
      });
    } catch (error) {
      console.error('Express Processing: Failed to refresh sections', error);
      // Fallback to page reload if section rendering fails
      window.location.reload();
    }
  }

  /**
   * Get sections to render (matches cart.js pattern)
   */
  getSectionsToRender() {
    const sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents'
      }
    ];

    return sections.filter(section => section.section);
  }

  /**
   * Parse HTML section
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  /**
   * Set loading state
   */
  setLoadingState(isLoading) {
    if (isLoading) {
      this.button.classList.add('loading');
      this.button.disabled = true;
    } else {
      this.button.classList.remove('loading');
      this.button.disabled = false;
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
      this.errorElement.classList.add('express-processing__error--visible');
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorElement) {
      this.errorElement.textContent = '';
      this.errorElement.classList.remove('express-processing__error--visible');
    }
  }

  /**
   * Cleanup when element is removed
   */
  disconnectedCallback() {
    console.log('🔌 [Express Processing] ============================================');
    console.log('🔌 [Express Processing] Element DISCONNECTED from DOM at:', new Date().toISOString());
    console.log('🔌 [Express Processing] ============================================');
    document.removeEventListener('cart:updated', this.debouncedCartUpdate);

    // Reset initialized flag so it can be re-initialized when reconnected
    this.initialized = false;
  }
}

// Register custom element (only once)
if (typeof customElements !== 'undefined') {
  if (!customElements.get('express-processing-prompt')) {
    customElements.define('express-processing-prompt', ExpressProcessingPrompt);
    console.log('✅ [Express Processing] Custom element registered: <express-processing-prompt>');
  } else {
    console.log('ℹ️  [Express Processing] Custom element already registered, skipping...');
  }
} else {
  console.error('❌ [Express Processing] customElements API not available');
}
